import React, { useMemo, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { createUseStyles } from 'react-jss'
import cn from 'classnames'
import { getProducts } from '../../redux/slices/content'
import { sliceMarginStyles, span } from '../../style/span'
import theme from '../../style/theme'
import RelatedProductCard from '../RelatedProductCard'
import { vw } from '../../style/vw'
import { useQueryState } from '../../hooks/useQueryState'

const ProductListSliceShopify = ({ className }) => {
  const classes = useStyles()
  const products = useSelector(getProducts)
  const [isAnimating, setIsAnimating] = useState(false)

  const [selectedFilter, setSelectedFilter] = useQueryState('filter', {
    defaultValue: 'All'
  })

  const filterList = useMemo(() => {
    return ['All', ...new Set(products.map(product => product.store.productType).filter(Boolean))]
  }, [products])

  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      // Filter out inactive/archived products
      const isActive = product.store?.status === 'active' || product.store?.status === 'draft'
      if (!isActive) return false
      // Apply category filter
      if (selectedFilter === 'All') return true
      return product.store.productType === selectedFilter
    })
  }, [products, selectedFilter])

  // Simple animation trigger
  useEffect(() => {
    setIsAnimating(true)
    const timer = setTimeout(() => setIsAnimating(false), 50)
    return () => clearTimeout(timer)
  }, [selectedFilter])

  return (
    <section className={cn(className, classes.section)}>
      <Filter
        filterList={filterList}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
      />
      <div className={cn(classes.grid, { [classes.gridAnimating]: isAnimating })}>
        {!filteredProducts || filteredProducts.length === 0 ? (
          <p className={classes.noProducts}> </p>
        ) : (
          filteredProducts.map((product, i) => {
            return (
              <div
                key={`${product._id || i}-${selectedFilter}`} // Key change forces re-mount
                className={classes.productCard}
                style={{ animationDelay: `${i * 50}ms` }} // Stagger effect
              >
                <RelatedProductCard product={product} size='small' />
              </div>
            )
          })
        )}
      </div>
    </section>
  )
}

// Update the Filter component to receive props instead of using its own hook
const Filter = ({ filterList, selectedFilter, setSelectedFilter }) => {
  const classes = useStyles()

  const handleSelectChange = (e) => {
    setSelectedFilter(e.target.value)
  }

  const handleListClick = (option) => {
    setSelectedFilter(option)
  }

  return (
    <div className={classes.filterContainer}>
      <h3 className={classes.filterTitle}>Filter by</h3>
      {/* Mobile Select Dropdown */}
      <select
        className={classes.filterSelect}
        value={selectedFilter}
        onChange={handleSelectChange}
      >
        {filterList.map(option => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
      {/* Desktop List */}
      <ul className={classes.filterOptions}>
        {filterList.map(option => (
          <li
            key={option}
            className={cn({ active: selectedFilter === option })}
            onClick={() => handleListClick(option)}
          >
            <span>{option}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

const useStyles = createUseStyles({
  section: {
    extend: [sliceMarginStyles],
    display: 'flex',
    borderTop: 'none',
    padding: '2rem 1rem',
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      borderTop: `1px solid ${theme.colors.border}`,
      padding: [span(1, 'md'), span(1, 'md'), span(1, 'md')],
      flexDirection: 'row'
    }
  },
  title: {
    marginBottom: '2rem',
    textAlign: 'center',
    fontFamily: theme.fonts.headings,
    fontWeight: theme.fonts.headingsFontWeight
  },
  noProducts: {
    textAlign: 'center',
    color: theme.colors.grey
  },
  filterContainer: {
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: span(6, 'md')
    }
  },
  filterList: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  filterTitle: {
    fontFamily: theme.fonts.body,
    fontSize: vw(14, 'mobile'),
    color: theme.colors.primary,
    [theme.breakpoints.up('md')]: {
      fontSize: vw(14, 'desktop')
    }
  },
  filterSelect: {
    display: 'block',
    width: '100%',
    padding: '0.75rem',
    margin: '1rem 0',
    fontSize: vw(16, 'mobile'),
    fontFamily: theme.fonts.headings,
    fontWeight: 'bold',
    color: theme.colors.primary,
    backgroundColor: 'transparent',
    border: `1px solid ${theme.colors.primary}`,
    appearance: 'none',
    backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6,9 12,15 18,9\'%3e%3c/polyline%3e%3c/svg%3e")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 0.75rem center',
    backgroundSize: '1rem',
    [theme.breakpoints.up('md')]: {
      display: 'none'
    },

    '&:focus': {
      outline: 'none',
      borderColor: theme.colors.primary,
      opacity: 1
    }
  },
  filterOptions: {
    listStyle: 'none',
    padding: 0,
    margin: '2rem 0 0',
    display: 'none',

    [theme.breakpoints.up('md')]: {
      display: 'block'
    },

    '& li': {
      fontFamily: theme.fonts.headings,
      fontSize: vw(16, 'mobile'),
      lineHeight: '1.2em',
      color: theme.colors.primary,
      fontWeight: 'bold',
      opacity: 0.5,
      cursor: 'pointer',
      display: 'block',
      marginTop: vw(10),

      [theme.breakpoints.up('md')]: {
        fontSize: vw(16, 'desktop'),
        marginTop: vw(15, 'desktop')
      },

      '& span': {
        paddingBottom: vw(2.5),
        borderBottom: '1px solid transparent',

        [theme.breakpoints.up('md')]: {
          paddingBottom: vw(2.5, 'desktop')
        }
      },

      '&:hover': {
        opacity: 1
      },

      '&.active': {
        opacity: 1,

        '& span': {
          borderBottom: `1px solid ${theme.colors.primary}`
        }
      }
    }
  },
  grid: {
    display: 'grid',
    flex: 1,
    gridTemplateColumns: 'repeat(1, 1fr)',
    rowGap: vw(65),
    columnGap: '20px',
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
      rowGap: vw(65, 'desktop')
    },
    [theme.breakpoints.up('lg')]: {
      gridTemplateColumns: 'repeat(3, 1fr)'
    }
  },
  gridAnimating: {
    '& $productCard': {
      animation: '$fadeInUp 0.3s ease-out forwards'
    }
  },
  productCard: {
    opacity: 0,
    transform: 'translateY(20px)',
    animation: '$fadeInUp 0.3s ease-out forwards',
    // Fallback for when animation isn't triggered
    '&:nth-child(1)': { animationDelay: '0ms' },
    '&:nth-child(2)': { animationDelay: '50ms' },
    '&:nth-child(3)': { animationDelay: '100ms' },
    '&:nth-child(4)': { animationDelay: '150ms' },
    '&:nth-child(5)': { animationDelay: '200ms' },
    '&:nth-child(6)': { animationDelay: '250ms' },
    '&:nth-child(7)': { animationDelay: '300ms' },
    '&:nth-child(8)': { animationDelay: '350ms' },
    '&:nth-child(9)': { animationDelay: '400ms' },
    '&:nth-child(10)': { animationDelay: '450ms' },
    '& h3': {
      fontSize: vw(16),
      [theme.breakpoints.up('md')]: {
        fontSize: vw(16, 'desktop')
      }
    }
  },
  '@keyframes fadeInUp': {
    to: {
      opacity: 1,
      transform: 'translateY(0)'
    }
  },
  imageWrapper: {
    position: 'relative',
    width: '100%',
    paddingBottom: '100%', // 1:1 aspect ratio
    overflow: 'hidden'
  },
  image: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  details: {
    padding: '1.5rem'
  },
  productTitle: {
    fontFamily: theme.fonts.headings,
    fontSize: '1.25rem',
    marginTop: 0,
    marginBottom: '1rem',
    color: theme.colors.primary
  },
  priceContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '1rem'
  },
  price: {
    fontFamily: theme.fonts.body,
    fontSize: '1.125rem',
    fontWeight: 'bold',
    color: theme.colors.primary
  },
  comparePrice: {
    textDecoration: 'line-through',
    color: theme.colors.grey,
    fontWeight: 'normal'
  },
  outOfStock: {
    display: 'inline-block',
    padding: '0.5rem 1rem',
    backgroundColor: theme.colors.error,
    color: theme.colors.white,
    borderRadius: '4px',
    fontWeight: 'bold',
    fontSize: '0.875rem'
  },
  placeholder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '200px',
    backgroundColor: theme.colors.lightGrey,
    color: theme.colors.grey,
    fontStyle: 'italic'
  },
  productLink: {
    textDecoration: 'none',
    color: 'inherit',
    display: 'block',
    height: '100%'
  }
}, { name: 'ProductListSliceShopify' })

export default ProductListSliceShopify
